import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { track, identify, EVENT } from "@/lib/analytics";

/**
 * CollectorAuthContext — deliberately separate from AuthContext (admin).
 * Distinct cookies + distinct token types on the backend mean these two
 * providers never intersect. Do NOT merge them.
 */
const Ctx = createContext(null);

export function CollectorAuthProvider({ children }) {
  const [collector, setCollector] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks explicit logout so a background /me on the following render
  // does not silently restore a session the user just ended.
  const explicitLogoutRef = React.useRef(false);

  const refresh = useCallback(async () => {
    // Skip the probe on public order-tracker pages — they carry a magic
    // token instead and never need a collector session.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/orders/")) {
      setLoading(false);
      return;
    }
    // Session-restoration flow:
    //   1. GET /collector-auth/me
    //   2. On 401, attempt ONE POST /collector-auth/refresh
    //   3. If refresh succeeded, retry /me ONCE
    //   4. Any other outcome → logged out
    // A single attempt guard prevents refresh loops even if the retry
    // itself returns 401.
    if (explicitLogoutRef.current) {
      setCollector(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/collector-auth/me");
      setCollector(r.data.collector);
      if (r.data.collector?.username) identify(r.data.collector.username);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        // Try to restore via the refresh cookie — one attempt, no loop.
        try {
          await api.post("/collector-auth/refresh");
          const r2 = await api.get("/collector-auth/me");
          setCollector(r2.data.collector);
          if (r2.data.collector?.username) identify(r2.data.collector.username);
        } catch (_e2) {
          setCollector(null);
        }
      } else {
        // Network / 5xx — surface without leaking credentials, tokens, cookies.
        console.warn("Collector authentication token refresh failed.");
        setCollector(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password, remember) => {
    explicitLogoutRef.current = false;
    const r = await api.post("/collector-auth/login", { email, password, remember });
    setCollector(r.data.collector);
    if (r.data.collector?.username) identify(r.data.collector.username);
    track(EVENT.LOGIN, { method: "password" });
    return r.data.collector;
  }, []);

  const logout = useCallback(async () => {
    track(EVENT.LOGOUT);
    explicitLogoutRef.current = true;
    try { await api.post("/collector-auth/logout"); } catch (_) { /* ignore */ }
    setCollector(null);
    identify(null);
  }, []);

  return (
    <Ctx.Provider value={{ collector, loading, refresh, login, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export const useCollectorAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCollectorAuth must be used within CollectorAuthProvider");
  return v;
};
